/**
 * The `claude` CLI backend — subscription-billed, agentic, reads the branch with real tools.
 *
 * ⛔ ONE PROCESS PER DIMENSION. NOT `--agents`.
 *
 * spec.md §7.1 bets the fan-out on `claude -p --agents <json>`, on the reasoning that the CLI would
 * do the dispatch and collection for us. That bet was measured on 2026-09-15 against the installed
 * binary (2.1.272) and it does not pay, for three separate reasons — each on its own sufficient:
 *
 *   1. ⛔ `--agents` is INERT under this project's own tool policy. With the shipped default
 *      `tools = ["Read","Grep","Glob"]` the dispatch tool is not in the allowlist, so the agent
 *      definitions are silently ignored: `subagent_stats.spawned == 0`, exit 0, and a fluent
 *      paragraph of prose that reads exactly like a successful five-dimension review. Adding `Task`
 *      to the list does make it fan out (`spawned: 2`, `by_type: {counter:1, firster:1}`) — but the
 *      tools list is described in config.md as "THE MOST LOAD-BEARING DEFAULT IN THIS FILE", and
 *      widening it to buy fan-out is a poor trade when we can fan out ourselves for free.
 *   2. ⛔ Subagents launch ASYNCHRONOUSLY. The dispatch tool returns launch metadata, not the
 *      subagent's answer, and the orchestrator PARAPHRASES what comes back. Measured: a subagent
 *      that emitted the single token `2` reached the caller as "`notes.txt` has **2** lines". A
 *      paraphrase is survivable for a number and fatal for a findings document that must carry a
 *      byte-identical code quote (spec.md §6, "quote is honest" is a hard gate).
 *   3. ⛔ `--json-schema` constrains the ORCHESTRATOR's final answer only. There is no per-subagent
 *      schema enforcement, so the one mechanism that makes structured output trustworthy does not
 *      reach the dimensions at all.
 *
 * ⇒ What we do instead: one `claude -p` per dimension, `--system-prompt` carrying the cacheable
 * corpus and rubric, `--append-system-prompt` carrying the per-dimension instruction, and
 * `--json-schema` enforcing the finding shape on that dimension's own output. Measured to work:
 * `structured_output` comes back parsed, and a second invocation over the same prefix read 26,118
 * cached tokens while writing 269. Separability is by construction — a process per dimension — and
 * `[backend.claude_cli].max_processes` already anticipated this shape.
 *
 * ⚠️ Subagent output IS separable if a future stage needs it: `--output-format stream-json
 * --forward-subagent-text` tags every subagent message with `parent_tool_use_id`, which matches the
 * `Agent` tool_use whose `input.subagent_type` names the agent. Recorded so it is not re-derived.
 * ⚠️ That stream also emits MULTIPLE `result` events (three, in the probe: `result_index` 0,1,2),
 * the first of which said only "I've started both subagents". Anything reading "the result" from a
 * stream-json run must take the last one, not the first.
 */

import { execFile } from "node:child_process"
import { CREDENTIAL_SHAPES, type ClaudeCliConfig } from "../config/schema.js"
import { cacheStats } from "./cache.js"
import {
  BackendAuthError,
  BackendError,
  ProbeFailedError,
  type Backend,
  type BackendRequest,
  type BackendResult,
  type StopReason,
} from "./types.js"

export interface SpawnOutcome {
  readonly stdout: string
  readonly stderr: string
  /** null when the child was killed by a signal. */
  readonly code: number | null
  /** Set when the child could not be started or was killed — a spawn failure, not a CLI verdict. */
  readonly spawnError?: Error
}

export interface SpawnOptions {
  readonly cwd?: string
  /** ⛔ The full environment for the child, constructed by us. Never `process.env` by reference. */
  readonly env: NodeJS.ProcessEnv
  readonly timeoutMs: number
}

/** Injected so the test suite never forks a real process. */
export type SpawnFn = (
  bin: string,
  args: readonly string[],
  opts: SpawnOptions,
) => Promise<SpawnOutcome>

/**
 * ⚠️ 64 MiB. Node's default `maxBuffer` is 1 MiB, and a five-dimension findings document over a
 * large diff exceeds that — at which point the child is KILLED and the output is truncated, which
 * presents as a malformed-JSON parse error three layers away from the cause.
 */
const MAX_BUFFER = 64 * 1024 * 1024

/**
 * ⛔ Spawned with an ARGUMENT ARRAY and no shell. There is therefore no interpolation surface: a
 * diff containing `$(rm -rf /)` is one argv element, not a command.
 */
export const defaultSpawn: SpawnFn = (bin, args, opts) =>
  new Promise((resolve) => {
    execFile(
      bin,
      [...args],
      {
        ...(opts.cwd === undefined ? {} : { cwd: opts.cwd }),
        env: opts.env,
        timeout: opts.timeoutMs,
        maxBuffer: MAX_BUFFER,
        encoding: "utf8",
        shell: false,
      },
      (err, stdout, stderr) => {
        // ⛔ A non-zero exit is NOT resolved here into a verdict. The caller reads stdout and decides,
        // because this CLI's exit code carries no auth information in either direction.
        const code = err === null ? 0 : ((err as NodeJS.ErrnoException & { code?: number }).code ?? null)
        resolve({
          stdout,
          stderr,
          code: typeof code === "number" ? code : null,
          ...(err !== null && typeof code !== "number" ? { spawnError: err } : {}),
        })
      },
    )
  })

/**
 * Variadic flags on this CLI consume every following token that does not look like an option.
 *
 * ⛔ THE PROMPT IS A POSITIONAL ARGUMENT, not the value of `-p` (which is the boolean `--print`).
 * Emit it after `--tools Read Grep Glob` and the prompt becomes a fourth tool name — the CLI then
 * runs with no prompt and an allowlist containing the entire diff. Guarded structurally below.
 */
const VARIADIC_FLAGS = new Set([
  "--tools",
  "--add-dir",
  "--allowedTools",
  "--allowed-tools",
  "--disallowedTools",
  "--disallowed-tools",
  "--mcp-config",
  "--plugin-dir",
  "--plugin-url",
  "--file",
  "--betas",
])

/**
 * ⛔ `ps` is world-readable on a shared machine. A credential goes in the child's ENVIRONMENT and
 * never in argv — config.md §5. This is a structural check rather than a convention, because the
 * convention has been broken before and the failure is silent and permanent.
 */
export function assertNoCredentialInArgv(args: readonly string[]): void {
  for (const arg of args) {
    for (const shape of CREDENTIAL_SHAPES) {
      if (shape.test(arg)) {
        // ⛔ Names the pattern, never the value.
        throw new BackendError(
          `a credential-shaped value reached the claude CLI argument list (pattern ${shape.source}); ` +
            `credentials go in the child environment, never argv — ps is world-readable`,
          "claude_cli",
        )
      }
    }
  }
}

/** The prompt handed to the CLI. The instruction proper travels in the system prompt. */
const RUN_PROMPT =
  "Review the diff in your instructions against the surrounding code and emit findings that match the required schema."

/**
 * Build the argument array for one dimension.
 *
 * ⭐ Exported so the argv is testable without forking anything. The shape of this array is the whole
 * security posture of the CLI backend: read-only tools, restricted mode, nobody at the keyboard.
 */
export function buildArgs(cfg: ClaudeCliConfig, req: BackendRequest): string[] {
  const system = [req.systemPrompt.corpus, req.systemPrompt.rubric].filter((s) => s !== "").join("\n\n")

  // ⛔ Prompt FIRST, immediately after `-p`, so no variadic flag can ever swallow it.
  const args: string[] = ["-p", RUN_PROMPT]

  args.push("--system-prompt", `${system}\n\n--- diff under review ---\n${req.diff}`)
  // ⭐ The per-dimension instruction is APPENDED, so it renders after the cacheable prefix. Putting
  // it in --system-prompt would move it into the cached block and buy a fresh write every call.
  args.push("--append-system-prompt", req.dimensionInstruction)

  args.push("--output-format", cfg.outputFormat)
  if (cfg.jsonSchemaEnforce) args.push("--json-schema", JSON.stringify(req.outputSchema))
  args.push("--model", req.model ?? cfg.model)
  if (cfg.fallbackModel.length > 0) args.push("--fallback-model", cfg.fallbackModel.join(","))
  args.push("--effort", req.effort ?? cfg.effort)
  args.push("--permission-mode", cfg.permissionMode)
  // ⛔ Nobody is at the keyboard during a headless review. Anything that would prompt is DENIED,
  // not left hanging until the 15-minute timeout expires.
  args.push("--permission-prompts", cfg.permissionPrompts)
  if (cfg.restricted) args.push("--restricted")
  if (cfg.bare) args.push("--bare")
  if (cfg.strictMcpConfig) args.push("--strict-mcp-config")
  if (!cfg.sessionPersistence) args.push("--no-session-persistence")
  if (cfg.settingSources.length > 0) args.push("--setting-sources", cfg.settingSources.join(","))
  // ⚠️ 0 means uncapped, which is what subscription billing implies — so 0 emits no flag at all
  // rather than `--max-budget-usd 0`, which would cap every call at zero dollars.
  if (cfg.maxBudgetUsd > 0) args.push("--max-budget-usd", String(cfg.maxBudgetUsd))

  // Variadic flags last, and never immediately before a positional.
  if (cfg.tools.length > 0) args.push("--tools", ...cfg.tools)
  if (cfg.addDir.length > 0) args.push("--add-dir", ...cfg.addDir)
  args.push(...cfg.extraArgs)

  assertPromptIsNotSwallowed(args)
  assertNoCredentialInArgv(args)
  return args
}

function assertPromptIsNotSwallowed(args: readonly string[]): void {
  const promptIndex = args.indexOf(RUN_PROMPT)
  if (promptIndex !== 1 || args[0] !== "-p") {
    throw new BackendError(
      `the prompt must be argv[1], immediately after -p; found it at ${promptIndex}. ` +
        `A variadic flag before a positional consumes it silently.`,
      "claude_cli",
    )
  }
  for (const [i, a] of args.entries()) {
    if (VARIADIC_FLAGS.has(a) && i > promptIndex) continue
    if (VARIADIC_FLAGS.has(a) && i < promptIndex) {
      throw new BackendError(`variadic flag ${a} precedes the prompt and would swallow it`, "claude_cli")
    }
  }
}

/** The CLI's `--output-format json` envelope. Only the fields we actually consume are named. */
interface CliEnvelope {
  result?: unknown
  structured_output?: unknown
  is_error?: boolean
  subtype?: string
  stop_reason?: string
  total_cost_usd?: number
  permission_denials?: unknown[]
  usage?: {
    input_tokens?: number
    output_tokens?: number
    cache_creation_input_tokens?: number
    cache_read_input_tokens?: number
    cache_creation?: { ephemeral_1h_input_tokens?: number; ephemeral_5m_input_tokens?: number }
  }
  subagent_stats?: { spawned?: number }
}

/**
 * Markers the CLI prints on stdout when it is not authenticated.
 *
 * ⚠️ It exits 0 while printing these. Measured in verdict and recorded in config.md §3. Reading
 * the exit code here is the documented way to conclude "authenticated" about a signed-out machine.
 */
const LOGGED_OUT_MARKERS = [/not logged in/i, /please run .{0,12}claude (auth )?login/i, /invalid api key/i]

export interface ClaudeCliOptions {
  readonly config: ClaudeCliConfig
  readonly spawn?: SpawnFn
  /**
   * The child's environment. ⛔ Constructed by the caller so a credential is injected per child and
   * never inherited from a stale parent (config.md §5).
   */
  readonly env?: NodeJS.ProcessEnv
  readonly now?: () => number
}

export class ClaudeCliBackend implements Backend {
  readonly kind = "claude_cli" as const
  private readonly cfg: ClaudeCliConfig
  private readonly spawn: SpawnFn
  private readonly env: NodeJS.ProcessEnv
  private readonly now: () => number

  constructor(opts: ClaudeCliOptions) {
    this.cfg = opts.config
    this.spawn = opts.spawn ?? defaultSpawn
    this.env = opts.env ?? process.env
    this.now = opts.now ?? Date.now
  }

  /**
   * ⛔ ONE RETRY, ON A NAMED MODEL, AND THE RESULT SAYS SO.
   *
   * `--fallback-model` is handed to the CLI, but it only covers an OVERLOADED model. A model that
   * REFUSES is not an overload and the CLI does not fall back for it — measured 2026-09-15 on a live
   * pull request, where all five dimensions returned `is_error` with "Opus 5's safeguards flagged
   * this message" over an ordinary code diff. Without this retry the shipped default cannot complete
   * a review at all.
   *
   * ⚠️ The retry is deliberately NOT silent. `modelFallbackFrom` travels on the result, run.ts turns
   * it into a stated coverage note, and the report names the model that actually answered. A review
   * quietly produced by a different model than the configuration names is a review whose provenance
   * is wrong, and provenance is the thing that makes a finding checkable a week later.
   */
  async run(req: BackendRequest): Promise<BackendResult> {
    try {
      return await this.runOnce(req, req.model ?? this.cfg.model)
    } catch (err) {
      const next = this.cfg.fallbackModel[0]
      // ⛔ ONLY a model-level refusal falls back. An auth failure or a spawn failure must surface as
      // itself: retrying those on another model turns "your credential is wrong" into "the review
      // found nothing", which is the substitution this repository is organised against.
      if (
        next === undefined ||
        !(err instanceof BackendError) ||
        err instanceof BackendAuthError ||
        err instanceof ProbeFailedError
      ) {
        throw err
      }
      const from = req.model ?? this.cfg.model
      if (next === from) throw err
      const r = await this.runOnce(req, next)
      return { ...r, modelFallbackFrom: from, modelFallbackReason: err.message }
    }
  }

  private async runOnce(req: BackendRequest, model: string): Promise<BackendResult> {
    const args = buildArgs(this.cfg, { ...req, model })
    const started = this.now()
    const out = await this.spawn(this.cfg.bin, args, {
      ...(req.workdir === undefined ? {} : { cwd: req.workdir }),
      env: this.env,
      timeoutMs: this.cfg.timeoutMs,
    })
    const durationMs = this.now() - started

    if (out.spawnError !== undefined) {
      // ⛔ Could not RUN. Says nothing about the credential.
      throw new ProbeFailedError(
        `could not run ${this.cfg.bin}: ${out.spawnError.message}`,
        "claude_cli",
        out.stderr.slice(0, 400),
      )
    }

    const combined = `${out.stdout}\n${out.stderr}`
    if (LOGGED_OUT_MARKERS.some((re) => re.test(combined))) {
      // ⛔ Note that this branch is reachable on exit 0. That is the whole point.
      throw new BackendAuthError(
        `the claude CLI reports it is not authenticated (it exited ${out.code}, which is not evidence either way). ` +
          `A background process often cannot read the login keychain; \`claude setup-token\` is the fix.`,
        "claude_cli",
        out.stdout.slice(0, 400),
      )
    }

    let env: CliEnvelope
    try {
      env = JSON.parse(out.stdout) as CliEnvelope
    } catch {
      throw new BackendError(
        `the claude CLI did not produce parseable ${this.cfg.outputFormat} on stdout (exit ${out.code})`,
        "claude_cli",
        out.stdout.slice(0, 400),
      )
    }

    if (env.is_error === true) {
      // ⛔ THE REASON GOES IN THE MESSAGE, NOT ONLY IN `evidence`. Measured 2026-09-15 on a live run
      // against a real pull request: this line printed "the claude CLI reported an error (subtype
      // success)" five times and stopped there. `subtype` is the CLI's own vocabulary, it read
      // "success" while `is_error` was true, and the actual cause — the API had flagged the request
      // — sat in `result` where nothing printed it. An error that hides its own evidence sends the
      // reader to debug the wrong layer, which is this repository's reporting rule for findings
      // applied to its own failures.
      const reason = String(env.result ?? "").trim()
      const first = reason.split("\n").find((l) => l.trim() !== "") ?? ""
      // ⚠️ BOTH, not one or the other. The subtype is the CLI's own classification and is worth
      // keeping — `error_max_turns` is actionable on its own. It is simply not sufficient, because
      // it read "success" in the case that motivated this.
      throw new BackendError(
        first === ""
          ? `the claude CLI reported an error with no explanation (subtype ${env.subtype ?? "unknown"}, exit ${out.code})`
          : `the claude CLI reported an error (subtype ${env.subtype ?? "unknown"}): ${first.slice(0, 300)}`,
        "claude_cli",
        reason.slice(0, 2000),
      )
    }

    // ⚠️ The dimension must have done its own work. A spawned subagent here means someone widened
    // the tool allowlist and the review is no longer the shape this module documents.
    const spawned = env.subagent_stats?.spawned ?? 0
    if (spawned > 0) {
      throw new BackendError(
        `the claude CLI spawned ${spawned} subagent(s); this backend runs one dimension per process ` +
          `and subagent output is a paraphrase, which cannot carry a byte-identical quote`,
        "claude_cli",
      )
    }

    const u = env.usage ?? {}
    const created = u.cache_creation_input_tokens ?? 0
    const read = u.cache_read_input_tokens ?? 0

    // ⛔ structured_output, not a re-parse of `result`. Measured: with --json-schema the CLI returns
    // both, `result` as a JSON *string* and `structured_output` already parsed.
    const output = env.structured_output ?? parseResultAsJson(env.result)

    return {
      backend: "claude_cli",
      dimensionId: req.dimensionId,
      // ⛔ THE MODEL THAT ACTUALLY RAN, which is the `model` argument — not `req.model`, which is
      // what was ASKED for. Measured 2026-09-15 on a live review: after the fallback fired, the
      // report read "claude-opus-5 did not answer, so claude-opus-5 reviewed it instead", and every
      // finding's provenance named a model that had refused the request. Re-deriving a value the
      // caller already resolved is how a field ends up contradicting its own documentation.
      model,
      effort: req.effort ?? this.cfg.effort,
      output,
      raw: typeof env.result === "string" ? env.result : JSON.stringify(env.result ?? null),
      stopReason: normaliseStopReason(env.stop_reason),
      usage: { inputTokens: u.input_tokens ?? 0, outputTokens: u.output_tokens ?? 0 },
      cache: cacheStats({
        createdTokens: created,
        readTokens: read,
        uncachedInputTokens: u.input_tokens ?? 0,
        created1hTokens: u.cache_creation?.ephemeral_1h_input_tokens ?? 0,
        created5mTokens: u.cache_creation?.ephemeral_5m_input_tokens ?? 0,
      }),
      durationMs,
      ...(typeof env.total_cost_usd === "number" ? { costUsd: env.total_cost_usd } : {}),
      permissionDenials: (env.permission_denials ?? []).map((d) =>
        typeof d === "string" ? d : JSON.stringify(d),
      ),
    }
  }
}

function parseResultAsJson(result: unknown): unknown {
  if (typeof result !== "string") return result ?? null
  try {
    return JSON.parse(result)
  } catch {
    return result
  }
}

/**
 * ⛔ An exhaustive mapping needs a runtime floor. A value outside the union falls through a bare
 * switch, returns undefined, and the TypeError surfaces somewhere else entirely.
 */
function normaliseStopReason(raw: unknown): StopReason {
  switch (raw) {
    case "end_turn":
    case "max_tokens":
    case "refusal":
    case "stop_sequence":
    case "tool_use":
      return raw
    default:
      return "other"
  }
}
